import { HighlightStyle, syntaxHighlighting } from '@codemirror/language';
import {
  Decoration,
  EditorView,
  MatchDecorator,
  ViewPlugin,
  type DecorationSet,
  type ViewUpdate
} from '@codemirror/view';
import { tags } from '@lezer/highlight';

/**
 * Editor chrome on the `--z-*` tokens, so CodeMirror follows the app theme
 * (DESIGN.md §8.9: mono 12/20, 40px gutter, info-blue `@refs`).
 */
export const ziggizEditorTheme = EditorView.theme({
  '&': {
    color: 'var(--z-text-secondary)',
    backgroundColor: 'transparent',
    fontSize: '12px'
  },
  '&.cm-focused': { outline: 'none' },
  '.cm-scroller': {
    fontFamily: 'var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace)',
    lineHeight: '20px'
  },
  '.cm-content': { padding: '12px 0', caretColor: 'var(--z-brand)' },
  '.cm-cursor, .cm-dropCursor': { borderLeftColor: 'var(--z-brand)' },
  '&.cm-focused .cm-selectionBackground, .cm-selectionBackground, ::selection': {
    backgroundColor: 'var(--z-brand-subtle) !important'
  },
  '.cm-activeLine': { backgroundColor: 'var(--z-surface-hover)' },
  '.cm-gutters': {
    backgroundColor: 'transparent',
    border: 'none',
    color: 'var(--z-text-disabled)'
  },
  '.cm-lineNumbers .cm-gutterElement': { minWidth: '40px', padding: '0 12px 0 0' },
  '.cm-activeLineGutter': {
    backgroundColor: 'transparent',
    color: 'var(--z-text-secondary)'
  },
  '.cm-placeholder': { color: 'var(--z-text-muted)', fontFamily: 'var(--z-font-sans)' },
  '.cm-ref': { color: 'var(--z-info)' },
  '.cm-tooltip': {
    border: '1px solid var(--z-border-strong)',
    borderRadius: '10px',
    backgroundColor: 'var(--z-surface-solid)',
    overflow: 'hidden'
  },
  '.cm-tooltip-autocomplete > ul': {
    fontFamily: 'var(--font-mono, ui-monospace, monospace)',
    fontSize: '12px',
    padding: '4px'
  },
  '.cm-tooltip-autocomplete > ul > li': {
    borderRadius: '6px',
    padding: '3px 8px',
    color: 'var(--z-text-primary)'
  },
  '.cm-tooltip-autocomplete > ul > li[aria-selected]': {
    backgroundColor: 'var(--z-brand-subtle)',
    color: 'var(--z-brand)'
  },
  '.cm-completionDetail': { color: 'var(--z-text-muted)', fontStyle: 'normal' }
});

/** Highlight roles from DESIGN.md §8.9, matching `CodeBlock`. */
export const ziggizHighlighting = syntaxHighlighting(
  HighlightStyle.define([
    {
      tag: [tags.keyword, tags.operatorKeyword, tags.bool, tags.null],
      color: 'var(--z-brand)'
    },
    {
      tag: [tags.function(tags.variableName), tags.standard(tags.name)],
      color: 'var(--z-text-primary)',
      fontWeight: '500'
    },
    { tag: [tags.string, tags.number], color: 'var(--z-warning)' },
    { tag: [tags.typeName, tags.special(tags.name)], color: 'var(--z-info)' },
    {
      tag: [tags.comment, tags.lineComment, tags.blockComment],
      color: 'var(--z-text-muted)',
      fontStyle: 'italic'
    },
    {
      tag: [tags.propertyName, tags.definition(tags.propertyName)],
      color: 'var(--z-text-primary)'
    }
  ])
);

const refMatcher = new MatchDecorator({
  regexp: /@\w+/g,
  decoration: Decoration.mark({ class: 'cm-ref' })
});

/** Colours `@previous_step` and friends, whatever the language grammar calls them. */
export const highlightRefs = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = refMatcher.createDeco(view);
    }
    update(update: ViewUpdate) {
      this.decorations = refMatcher.updateDeco(update, this.decorations);
    }
  },
  { decorations: (plugin) => plugin.decorations }
);
